import { useCallback, useEffect, useRef, useState } from 'react';

import { getAccessToken } from '@/lib/api/axios';
import { getEnv } from '@/lib/env';

type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'ended'
  | 'not_live'
  | 'error';

type MonitorRole = 'user' | 'assistant';
/** Voice calls stream audio + WebRTC takeover; text channels (WhatsApp) stream transcript + typed takeover. */
type MonitorChannel = 'voice' | 'text';

interface ReadyMessage {
  type: 'ready';
}

interface SubscribedMessage {
  type: 'subscribed';
  callId: string;
  callSid: string;
  channel: MonitorChannel;
  replayCount: number;
}

interface NotLiveMessage {
  type: 'not_live';
  callId: string;
}

interface TranscriptMessage {
  type: 'transcript';
  role: MonitorRole;
  content: string;
  timestamp: string;
  replay?: boolean;
}

interface AudioMessage {
  type: 'audio';
  track: 'inbound' | 'outbound';
  codec: 'pcm16le' | 'mulaw';
  sampleRate: number;
  sequence: number;
  payload: string;
  timestamp: number;
}

interface EndedMessage {
  type: 'ended';
  callId?: string;
  reason: string;
  endedAt: string;
}

interface ErrorMessage {
  type: 'error';
  error: string;
}

interface PongMessage {
  type: 'pong';
}

interface TakeoverStateMessage {
  type: 'takeover_state';
  active: boolean;
}

interface WebRtcStateMessage {
  type: 'webrtc_state';
  enabled: boolean;
  mode: 'webrtc';
  status: 'idle' | 'negotiating' | 'connected' | 'unsupported' | 'error';
  reason?: string;
}

interface WebRtcAnswerMessage {
  type: 'webrtc_answer';
  sdp: string;
}

interface WebRtcIceCandidateMessage {
  type: 'webrtc_ice_candidate';
  candidate: RTCIceCandidateInit | null;
}

interface WebRtcIceServersMessage {
  type: 'webrtc_ice_servers';
  servers: RTCIceServer[];
}

type MonitorServerMessage =
  | ReadyMessage
  | SubscribedMessage
  | NotLiveMessage
  | TranscriptMessage
  | AudioMessage
  | EndedMessage
  | ErrorMessage
  | PongMessage
  | TakeoverStateMessage
  | WebRtcStateMessage
  | WebRtcAnswerMessage
  | WebRtcIceCandidateMessage
  | WebRtcIceServersMessage;

interface TranscriptEntry {
  id: string;
  role: MonitorRole;
  content: string;
  timestamp: string;
  replay: boolean;
}

export type LiveMonitorConnectionState = ConnectionState;
export type LiveMonitorTranscriptEntry = TranscriptEntry;
export type LiveMonitorChannel = MonitorChannel;

interface UseLiveCallMonitorResult {
  isListening: boolean;
  isTakeoverActive: boolean;
  connectionState: ConnectionState;
  channel: MonitorChannel;
  transcript: TranscriptEntry[];
  error: string | null;
  startListening: () => Promise<void>;
  stopListening: () => void;
  startTakeover: () => Promise<void>;
  stopTakeover: () => void;
  sendOperatorMessage: (text: string) => boolean;
  clearTranscript: () => void;
}

interface WebkitAudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const MAX_RECONNECT_ATTEMPTS = 3;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 8000;
const PLAYBACK_LEAD_SECONDS = 0.04;
const MAX_TRANSCRIPT_ENTRIES = 400;
const MAX_TRACK_BACKLOG_SECONDS = 8;
const DEFAULT_OPERATOR_WEBRTC_STUN_URL = 'stun:stun.l.google.com:19302';
const ICE_SERVER_SIGNAL_TIMEOUT_MS = 2500;

function decodeMuLawSample(encodedByte: number): number {
  const ulaw = ~encodedByte & 0xff;
  const signBit = ulaw & 0x80;
  const exponent = (ulaw >> 4) & 0x07;
  const mantissa = ulaw & 0x0f;
  const bias = 0x84;

  let sample = ((mantissa << 3) + bias) << exponent;
  sample = signBit ? bias - sample : sample - bias;

  return sample;
}

function decodeMulawToPcm(base64Payload: string): Float32Array {
  const binaryString = atob(base64Payload);
  const sampleCount = binaryString.length;
  const pcm = new Float32Array(sampleCount);

  for (let index = 0; index < sampleCount; index += 1) {
    const encoded = binaryString.charCodeAt(index);
    pcm[index] = decodeMuLawSample(encoded) / 32768;
  }

  return pcm;
}

function decodePcm16LeToPcm(base64Payload: string): Float32Array {
  const binaryString = atob(base64Payload);
  const byteLength = binaryString.length;
  const sampleCount = Math.floor(byteLength / 2);

  if (sampleCount <= 0) {
    return new Float32Array(0);
  }

  const bytes = new Uint8Array(byteLength);
  for (let index = 0; index < byteLength; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  const dataView = new DataView(bytes.buffer);
  const pcm = new Float32Array(sampleCount);
  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
    const sample = dataView.getInt16(sampleIndex * 2, true);
    pcm[sampleIndex] = sample / 32768;
  }

  return pcm;
}

function buildTranscriptKey(role: MonitorRole, content: string, timestamp: string): string {
  return `${role}|${timestamp}|${content}`;
}

export function useLiveCallMonitor(callId: string | undefined): UseLiveCallMonitorResult {
  const [isListening, setIsListening] = useState(false);
  const [isTakeoverActive, setIsTakeoverActive] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [channel, setChannel] = useState<MonitorChannel>('voice');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<MonitorChannel>('voice');

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalStopRef = useRef(false);
  const listeningRef = useRef(false);
  const takeoverActiveRef = useRef(false);
  const takeoverRequestedRef = useRef(false);
  const connectRef = useRef<(isReconnect: boolean) => void>(() => {});

  const webRtcIceServersRef = useRef<RTCIceServer[]>([{ urls: DEFAULT_OPERATOR_WEBRTC_STUN_URL }]);
  const pendingIceServerSignalResolverRef = useRef<(() => void) | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef<{ inbound: number; outbound: number }>({
    inbound: 0,
    outbound: 0,
  });
  const transcriptSeenRef = useRef<Set<string>>(new Set());
  const lastAudioSequenceRef = useRef<{ inbound: number; outbound: number }>({
    inbound: 0,
    outbound: 0,
  });
  const webRtcPeerRef = useRef<RTCPeerConnection | null>(null);
  const webRtcStreamRef = useRef<MediaStream | null>(null);
  const webRtcRemoteStreamRef = useRef<MediaStream | null>(null);
  const webRtcRemoteAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    listeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    takeoverActiveRef.current = isTakeoverActive;
  }, [isTakeoverActive]);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const closeAudioContext = useCallback(async () => {
    const context = audioContextRef.current;
    audioContextRef.current = null;
    nextPlayTimeRef.current = {
      inbound: 0,
      outbound: 0,
    };

    if (context) {
      await context.close();
    }
  }, []);

  const stopWebRtcTakeover = useCallback(() => {
    const peer = webRtcPeerRef.current;
    webRtcPeerRef.current = null;
    if (peer) {
      peer.onicecandidate = null;
      peer.onconnectionstatechange = null;
      peer.close();
    }

    const stream = webRtcStreamRef.current;
    webRtcStreamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    const remoteStream = webRtcRemoteStreamRef.current;
    webRtcRemoteStreamRef.current = null;
    if (remoteStream) {
      remoteStream.getTracks().forEach((track) => track.stop());
    }

    const remoteAudio = webRtcRemoteAudioRef.current;
    webRtcRemoteAudioRef.current = null;
    if (remoteAudio) {
      remoteAudio.srcObject = null;
    }
  }, []);

  const waitForWebRtcIceServersSignal = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        pendingIceServerSignalResolverRef.current = null;
        resolve();
      }, ICE_SERVER_SIGNAL_TIMEOUT_MS);

      pendingIceServerSignalResolverRef.current = () => {
        clearTimeout(timeout);
        pendingIceServerSignalResolverRef.current = null;
        resolve();
      };
    });
  }, []);

  const startWebRtcTakeover = useCallback(async (): Promise<boolean> => {
    const socket = wsRef.current;
    if (socket?.readyState !== WebSocket.OPEN) {
      return false;
    }

    if (typeof window === 'undefined' || typeof RTCPeerConnection === 'undefined') {
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const peer = new RTCPeerConnection({
        iceServers: webRtcIceServersRef.current,
        bundlePolicy: 'max-bundle',
      });

      const remoteStream = new MediaStream();
      const remoteAudio = new Audio();
      remoteAudio.autoplay = true;
      remoteAudio.srcObject = remoteStream;

      webRtcPeerRef.current = peer;
      webRtcStreamRef.current = stream;
      webRtcRemoteStreamRef.current = remoteStream;
      webRtcRemoteAudioRef.current = remoteAudio;

      stream.getTracks().forEach((track) => {
        peer.addTrack(track, stream);
      });

      peer.onicecandidate = (event) => {
        const activeSocket = wsRef.current;
        if (activeSocket?.readyState !== WebSocket.OPEN) {
          return;
        }

        activeSocket.send(
          JSON.stringify({
            type: 'webrtc_ice_candidate',
            candidate: event.candidate ? event.candidate.toJSON() : null,
          }),
        );
      };

      peer.onconnectionstatechange = () => {
        const state = peer.connectionState;
        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          setError(`WebRTC connection ${state}. Please retry takeover.`);
        }
      };

      peer.ontrack = (event) => {
        if (event.track.kind !== 'audio') {
          return;
        }

        const target = webRtcRemoteStreamRef.current;
        if (!target) {
          return;
        }

        target.addTrack(event.track);
        const audio = webRtcRemoteAudioRef.current;
        if (audio) {
          void audio.play().catch(() => {
            // Browser autoplay policies may require prior user gesture.
          });
        }
      };

      const offer = await peer.createOffer({ offerToReceiveAudio: true });
      await peer.setLocalDescription(offer);

      socket.send(
        JSON.stringify({
          type: 'webrtc_offer',
          sdp: offer.sdp ?? '',
        }),
      );

      return true;
    } catch {
      stopWebRtcTakeover();
      return false;
    }
  }, [stopWebRtcTakeover]);

  const closeSocket = useCallback(() => {
    const socket = wsRef.current;
    wsRef.current = null;

    if (!socket) {
      return;
    }

    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'unsubscribe' }));
    }

    socket.close(1000, 'client_stop');
  }, []);

  const appendTranscript = useCallback((message: TranscriptMessage) => {
    const key = buildTranscriptKey(message.role, message.content, message.timestamp);
    if (transcriptSeenRef.current.has(key)) {
      return;
    }

    transcriptSeenRef.current.add(key);

    setTranscript((previous) => {
      const next: TranscriptEntry = {
        id: key,
        role: message.role,
        content: message.content,
        timestamp: message.timestamp,
        replay: message.replay ?? false,
      };

      const merged = [...previous, next];
      if (merged.length > MAX_TRANSCRIPT_ENTRIES) {
        return merged.slice(merged.length - MAX_TRANSCRIPT_ENTRIES);
      }

      return merged;
    });
  }, []);

  const ensureAudioContext = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined') {
      setError('Live audio is only available in browser sessions.');
      return false;
    }

    if (!audioContextRef.current) {
      const audioWindow = window as WebkitAudioWindow;
      const AudioContextCtor = window.AudioContext || audioWindow.webkitAudioContext;

      if (!AudioContextCtor) {
        setError('Web Audio is not supported in this browser.');
        return false;
      }

      audioContextRef.current = new AudioContextCtor();
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    const nextStart = audioContextRef.current.currentTime + PLAYBACK_LEAD_SECONDS;
    nextPlayTimeRef.current = {
      inbound: Math.max(nextPlayTimeRef.current.inbound, nextStart),
      outbound: Math.max(nextPlayTimeRef.current.outbound, nextStart),
    };

    return true;
  }, []);

  const playAudioChunk = useCallback(async (message: AudioMessage) => {
    if (!message.payload || !audioContextRef.current) {
      return;
    }

    // During WebRTC takeover, browser audio comes from the peer track.
    // Skip monitor websocket playback to avoid duplicate/echoed audio locally.
    if (takeoverActiveRef.current && webRtcPeerRef.current) {
      return;
    }

    const lastSequence = lastAudioSequenceRef.current[message.track];
    if (message.sequence <= lastSequence) {
      return;
    }
    lastAudioSequenceRef.current[message.track] = message.sequence;

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    const pcm =
      message.codec === 'pcm16le'
        ? decodePcm16LeToPcm(message.payload)
        : decodeMulawToPcm(message.payload);
    if (pcm.length === 0) {
      return;
    }

    const context = audioContextRef.current;
    const safeSampleRate =
      Number.isFinite(message.sampleRate) && message.sampleRate > 0 ? message.sampleRate : 8000;
    const audioBuffer = context.createBuffer(1, pcm.length, safeSampleRate);
    audioBuffer.getChannelData(0).set(pcm);

    const source = context.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(context.destination);

    const track = message.track;
    const startTime = Math.max(
      context.currentTime + PLAYBACK_LEAD_SECONDS,
      nextPlayTimeRef.current[track],
    );
    source.start(startTime);

    nextPlayTimeRef.current[track] = startTime + audioBuffer.duration;

    if (nextPlayTimeRef.current[track] - context.currentTime > MAX_TRACK_BACKLOG_SECONDS) {
      nextPlayTimeRef.current[track] = context.currentTime + PLAYBACK_LEAD_SECONDS;
    }
  }, []);

  const attemptReconnect = useCallback(() => {
    if (intentionalStopRef.current || !listeningRef.current) {
      return;
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setConnectionState('error');
      setError('Live monitor disconnected. Please reconnect.');
      setIsListening(false);
      listeningRef.current = false;
      return;
    }

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** reconnectAttemptsRef.current,
      MAX_RECONNECT_DELAY_MS,
    );

    reconnectAttemptsRef.current += 1;
    setConnectionState('reconnecting');

    reconnectTimeoutRef.current = setTimeout(() => {
      connectRef.current(true);
    }, delay);
  }, []);

  const connect = useCallback(
    (isReconnect: boolean) => {
      if (!callId) {
        setConnectionState('error');
        setError('Missing call id for live monitoring.');
        setIsListening(false);
        listeningRef.current = false;
        return;
      }

      const token = getAccessToken();
      if (!token) {
        setConnectionState('error');
        setError('Authentication required. Please log in again.');
        setIsListening(false);
        listeningRef.current = false;
        return;
      }

      const apiBase = getEnv('VITE_API_URL');
      const apiOrigin = new URL(apiBase);
      const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiOrigin.host}/listen-ws/${encodeURIComponent(token)}`;

      if (!isReconnect) {
        setConnectionState('connecting');
      }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: 'subscribe',
            callId,
          }),
        );
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        let message: MonitorServerMessage;

        try {
          message = JSON.parse(event.data) as MonitorServerMessage;
        } catch {
          return;
        }

        switch (message.type) {
          case 'ready':
            return;
          case 'subscribed':
            reconnectAttemptsRef.current = 0;
            setConnectionState('connected');
            setChannel(message.channel);
            channelRef.current = message.channel;
            setError(null);
            if (takeoverRequestedRef.current) {
              ws.send(JSON.stringify({ type: 'takeover_start' }));
              // Voice takeover also negotiates a WebRTC audio bridge; text channels
              // (WhatsApp) just pause the AI and accept typed operator messages.
              if (message.channel === 'voice') {
                void (async () => {
                  await waitForWebRtcIceServersSignal();
                  await startWebRtcTakeover();
                })();
              }
            }
            return;
          case 'audio':
            void playAudioChunk(message);
            return;
          case 'transcript':
            appendTranscript(message);
            return;
          case 'takeover_state':
            setIsTakeoverActive(message.active);
            takeoverActiveRef.current = message.active;
            if (!message.active) {
              stopWebRtcTakeover();
            }
            return;
          case 'webrtc_ice_servers': {
            const fallbackServers: RTCIceServer[] = [{ urls: DEFAULT_OPERATOR_WEBRTC_STUN_URL }];
            webRtcIceServersRef.current = message.servers.length
              ? message.servers
              : fallbackServers;
            pendingIceServerSignalResolverRef.current?.();
            return;
          }
          case 'webrtc_state':
            if (message.status === 'unsupported' || message.status === 'error') {
              setError(message.reason || 'WebRTC negotiation failed.');
            }
            return;
          case 'webrtc_answer':
            if (webRtcPeerRef.current && message.sdp) {
              void webRtcPeerRef.current.setRemoteDescription({
                type: 'answer',
                sdp: message.sdp,
              });
            }
            return;
          case 'webrtc_ice_candidate':
            if (webRtcPeerRef.current && message.candidate) {
              void webRtcPeerRef.current.addIceCandidate(message.candidate);
            }
            return;
          case 'not_live':
            intentionalStopRef.current = true;
            setConnectionState('not_live');
            setError(null);
            setIsListening(false);
            setIsTakeoverActive(false);
            listeningRef.current = false;
            takeoverActiveRef.current = false;
            takeoverRequestedRef.current = false;
            closeSocket();
            void closeAudioContext();
            stopWebRtcTakeover();
            return;
          case 'ended':
            intentionalStopRef.current = true;
            setConnectionState('ended');
            setError(null);
            setIsListening(false);
            setIsTakeoverActive(false);
            listeningRef.current = false;
            takeoverActiveRef.current = false;
            takeoverRequestedRef.current = false;
            closeSocket();
            void closeAudioContext();
            stopWebRtcTakeover();
            return;
          case 'error':
            setConnectionState('error');
            setError(message.error);
            return;
          case 'pong':
            return;
          default:
            return;
        }
      };

      ws.onerror = () => {
        ws.close();
      };

      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        setIsTakeoverActive(false);
        takeoverActiveRef.current = false;
        stopWebRtcTakeover();

        if (intentionalStopRef.current || !listeningRef.current) {
          return;
        }

        attemptReconnect();
      };
    },
    [
      appendTranscript,
      attemptReconnect,
      callId,
      closeAudioContext,
      closeSocket,
      playAudioChunk,
      startWebRtcTakeover,
      stopWebRtcTakeover,
      waitForWebRtcIceServersSignal,
    ],
  );

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const startListening = useCallback(async () => {
    if (!callId || listeningRef.current) {
      return;
    }

    intentionalStopRef.current = false;
    reconnectAttemptsRef.current = 0;
    clearReconnectTimeout();

    setError(null);
    setIsListening(true);
    listeningRef.current = true;
    lastAudioSequenceRef.current = { inbound: 0, outbound: 0 };

    const audioReady = await ensureAudioContext();
    if (!audioReady) {
      setConnectionState('error');
      setIsListening(false);
      listeningRef.current = false;
      return;
    }

    connectRef.current(false);
  }, [callId, clearReconnectTimeout, ensureAudioContext]);

  const startTakeover = useCallback(async () => {
    if (!callId) {
      return;
    }

    takeoverRequestedRef.current = true;
    setError(null);

    if (!listeningRef.current) {
      await startListening();
    }

    const socket = wsRef.current;
    if (socket?.readyState !== WebSocket.OPEN) {
      setError('Unable to start takeover until live monitor is connected.');
      return;
    }

    // Text channels (WhatsApp): pause the AI and let the operator type. No WebRTC/mic.
    if (channelRef.current === 'text') {
      if (!takeoverActiveRef.current) {
        socket.send(JSON.stringify({ type: 'takeover_start' }));
        takeoverActiveRef.current = true;
        setIsTakeoverActive(true);
      }
      return;
    }

    if (!takeoverActiveRef.current) {
      const iceServersReady = waitForWebRtcIceServersSignal();
      socket.send(JSON.stringify({ type: 'takeover_start' }));
      await iceServersReady;
      takeoverActiveRef.current = true;
      setIsTakeoverActive(true);
    }

    const webRtcReady = await startWebRtcTakeover();
    if (webRtcReady) {
      return;
    }

    takeoverRequestedRef.current = false;
    takeoverActiveRef.current = false;
    setIsTakeoverActive(false);
    stopWebRtcTakeover();
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'takeover_stop' }));
    }
    setError('Unable to establish WebRTC takeover. Please retry.');
  }, [
    callId,
    startListening,
    startWebRtcTakeover,
    stopWebRtcTakeover,
    waitForWebRtcIceServersSignal,
  ]);

  const stopTakeover = useCallback(() => {
    takeoverRequestedRef.current = false;
    takeoverActiveRef.current = false;
    setIsTakeoverActive(false);

    const socket = wsRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'takeover_stop' }));
    }

    stopWebRtcTakeover();
  }, [stopWebRtcTakeover]);

  const stopListening = useCallback(() => {
    intentionalStopRef.current = true;
    clearReconnectTimeout();

    takeoverRequestedRef.current = false;
    takeoverActiveRef.current = false;
    setIsListening(false);
    setIsTakeoverActive(false);
    listeningRef.current = false;
    setConnectionState('idle');
    setError(null);

    closeSocket();
    void closeAudioContext();
    stopWebRtcTakeover();
  }, [clearReconnectTimeout, closeAudioContext, closeSocket, stopWebRtcTakeover]);

  const sendOperatorMessage = useCallback((text: string): boolean => {
    const trimmed = text.trim();
    if (!trimmed) {
      return false;
    }

    const socket = wsRef.current;
    if (socket?.readyState !== WebSocket.OPEN) {
      setError('Unable to send message until live monitor is connected.');
      return false;
    }

    socket.send(JSON.stringify({ type: 'operator_message', text: trimmed }));
    return true;
  }, []);

  const clearTranscript = useCallback(() => {
    transcriptSeenRef.current.clear();
    setTranscript([]);
  }, []);

  useEffect(() => {
    return () => {
      intentionalStopRef.current = true;
      takeoverRequestedRef.current = false;
      takeoverActiveRef.current = false;
      pendingIceServerSignalResolverRef.current?.();
      clearReconnectTimeout();
      closeSocket();
      void closeAudioContext();
      stopWebRtcTakeover();
    };
  }, [clearReconnectTimeout, closeAudioContext, closeSocket, stopWebRtcTakeover]);

  return {
    isListening,
    isTakeoverActive,
    connectionState,
    channel,
    transcript,
    error,
    startListening,
    stopListening,
    startTakeover,
    stopTakeover,
    sendOperatorMessage,
    clearTranscript,
  };
}
